import { Product } from "../@types";
import Service from "./Service";

export interface ProductListRequest {
  /** Abort this call after this many milliseconds. Omitted, or not a positive finite number, means no timer. */
  timeoutMs?: number;
  /** Abort this call when the caller aborts (unmount or retry). */
  signal?: AbortSignal;
}

export class ProductListTimeoutError extends Error {
  readonly timeoutMs: number;
  constructor(timeoutMs: number) {
    super(`GET /products aborted after ${timeoutMs}ms`);
    this.name = "ProductListTimeoutError";
    this.timeoutMs = timeoutMs;
    // The app compiles classes for older targets. Without this, `instanceof`
    // misses the subclass and an 8s abort is reported as a plain error.
    Object.setPrototypeOf(this, ProductListTimeoutError.prototype);
  }
}

export function isProductListTimeout(error: unknown): error is ProductListTimeoutError {
  if (error instanceof ProductListTimeoutError) return true;
  if (typeof error !== "object" || error === null) return false;
  const candidate = error as { name?: unknown; timeoutMs?: unknown };
  return candidate.name === "ProductListTimeoutError" && typeof candidate.timeoutMs === "number";
}

class ProductService extends Service {
  static getAll(request?: ProductListRequest) {
    const timeoutMs = request?.timeoutMs;
    const signal = request?.signal;

    if (typeof timeoutMs !== "number" || !Number.isFinite(timeoutMs) || timeoutMs <= 0) {
      if (signal == null) {
        return this.Http.get<Product.Product[]>("/products").then(this.getData);
      }
      return this.Http.get<Product.Product[]>("/products", { signal }).then(this.getData);
    }

    const controller = new AbortController();
    let settled = false;
    let timedOut = false;

    const onCallerAbort = () => {
      if (!settled) {
        controller.abort();
      }
    };

    const timer = window.setTimeout(() => {
      if (settled) {
        return;
      }
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    const cleanup = () => {
      settled = true;
      window.clearTimeout(timer);
      if (signal != null) {
        signal.removeEventListener("abort", onCallerAbort);
      }
    };

    if (signal != null) {
      signal.addEventListener("abort", onCallerAbort);
      if (signal.aborted) {
        onCallerAbort();
      }
    }

    try {
      return this.Http.get<Product.Product[]>("/products", { signal: controller.signal })
        .then(this.getData)
        .catch((error: unknown) => {
          if (timedOut) {
            throw new ProductListTimeoutError(timeoutMs);
          }
          throw error;
        })
        .finally(cleanup);
    } catch (error) {
      cleanup();
      throw error;
    }
  }

  static get(id: string) {
    return this.Http.get<Product.Product>(`/products/${id}`).then(this.getData);
  }

  static create(product: Product.Product) {
    return this.Http.post<Product.Product>("/products", product).then(this.getData);
  }

  static update(product: Product.Product) {
    return this.Http.put<Product.Product>(`/products/${product.id}`, product).then(this.getData);
  }

  static delete(product: Product.Product) {
    return this.Http.delete<Product.Product>(`/products/${product.id}`).then(this.getData);
  }
}

export default ProductService;
